/**
 * Removes the stale twins of the head tags `<PageHead/>` renders (web plan MW5).
 *
 * React 19 owns the `<title>`, `<meta>` and `<link>` tags a page renders: it
 * puts them in `document.head` and removes them with the page. A tag that is
 * already in the head and that React did not claim stays there for good: the
 * SSR copy of a page that was client-rendered instead of hydrated (the
 * `react-router dev` server), the prerendered file's tags (`dist/<route>/index.html`,
 * web plan W0.18), anything a script added. After the next in-app navigation
 * that left a wrong canonical, a wrong `hreflang` set or two `robots` next to
 * the page's own.
 *
 * `removeUnownedHeadTwins()` removes such a tag only when React owns another
 * tag with the same identity (one canonical, one tag per `hreflang`, per meta
 * `name` / `property`, one title), so it never removes the only copy of
 * anything and never touches a tag React renders. Ownership is read from the
 * properties React puts on every node it created or claimed (`__reactFiber$…`,
 * `__reactMarker$…`); if a React upgrade renamed them nothing would count as
 * owned and the function would remove nothing.
 */

/** The tags a page's head renders (see `<PageHead/>`). */
const MANAGED_SELECTOR = [
  'title',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'link[rel="canonical"]',
  'link[rel="alternate"][hreflang]',
].join(', ');

const OWNED_PROPERTY = /^__react(?:Fiber|Marker)\$/;

/** Whether React created or claimed this node. */
export function isReactOwned(node: Element): boolean {
  return Object.keys(node).some((key) => OWNED_PROPERTY.test(key));
}

/** The tag's identity: two tags with one key say the same thing. */
function identityOf(element: Element): string {
  const tag = element.tagName.toLowerCase();
  if (tag === 'title') return 'title';
  if (tag === 'link') {
    return element.getAttribute('rel') === 'canonical'
      ? 'canonical'
      : `alternate:${(element.getAttribute('hreflang') ?? '').toLowerCase()}`;
  }
  const property = element.getAttribute('property');
  return property ? `property:${property}` : `name:${element.getAttribute('name') ?? ''}`;
}

/** Removes the unowned twins of owned head tags; returns how many it removed. */
export function removeUnownedHeadTwins(doc: Document = document): number {
  const owned = new Set<string>();
  const unowned: Array<{ key: string; element: Element }> = [];
  for (const element of doc.head.querySelectorAll(MANAGED_SELECTOR)) {
    const key = identityOf(element);
    if (isReactOwned(element)) owned.add(key);
    else unowned.push({ key, element });
  }
  let removed = 0;
  for (const { key, element } of unowned) {
    if (owned.has(key)) {
      element.remove();
      removed += 1;
    }
  }
  return removed;
}
